import React, { useState, useEffect, useCallback } from 'react';
import GameSummary from './GameSummary';
import GameEventList from './GameEventList';
import { fetchRosterData } from '../utils/fetchRosterData';

const NHLGameLogViewer = () => {
    const [gameId, setGameId] = useState('');
    const [gameData, setGameData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [plays, setPlays] = useState([]);
    const [players, setPlayers] = useState([]);

    const fetchGameLog = useCallback(async (id) => {
        if (!id) {
            setError('Please enter a valid Game ID');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`https://fla-game-schedule-worker.soapcreativeservices.workers.dev/gamecenter/${id}/play-by-play`);
            if (!response.ok) throw new Error('Failed to fetch game log');
            const data = await response.json();
            setGameData(data);
            setPlays(data.plays || []);
            await fetchPlayers(data);
        } catch (err) {
            setError(err.message || 'An unknown error occurred');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPlayers = async (data) => {
        try {
            const homeTeamRoster = await fetchRosterData(data.homeTeam.abbrev, data.season);
            const awayTeamRoster = await fetchRosterData(data.awayTeam.abbrev, data.season);

            const homeTeamPlayers = [...homeTeamRoster.forwards, ...homeTeamRoster.defensemen, ...homeTeamRoster.goalies];
            const awayTeamPlayers = [...awayTeamRoster.forwards, ...awayTeamRoster.defensemen, ...awayTeamRoster.goalies];

            const allPlayers = [...homeTeamPlayers, ...awayTeamPlayers].map(player => ({
                id: player.id,
                fullName: `${player.firstName.default} ${player.lastName.default}`,
                teamAbbrev: player.headshot.split('/')[5] // Extract team abbreviation from headshot URL
            }));

            setPlayers(allPlayers);
        } catch (err) {
            setError('Failed to fetch player data');
        }
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const gameIdFromUrl = urlParams.get('gameid');
        if (gameIdFromUrl) {
            setGameId(gameIdFromUrl);
            fetchGameLog(gameIdFromUrl);
        }
    }, [fetchGameLog]);

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">NHL Game Log Viewer</h1>
            <div className="flex space-x-2 mb-4">
                <input
                    type="text"
                    placeholder="Enter NHL Game ID"
                    value={gameId}
                    onChange={(e) => setGameId(e.target.value)}
                    className="flex-grow p-2 border rounded"
                />
                <button
                    onClick={() => fetchGameLog(gameId)}
                    disabled={loading}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-blue-300"
                >
                    {loading ? 'Loading...' : 'Fetch Game Log'}
                </button>
            </div>
            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">{error}</div>}
            {gameData && (
                <>
                    <GameSummary gameData={gameData} plays={plays} />
                    <GameEventList
                        events={plays}
                        homeTeamId={gameData.homeTeam.id}
                        awayTeamId={gameData.awayTeam.id}
                        homeTeamAbbrev={gameData.homeTeam.abbrev}
                        awayTeamAbbrev={gameData.awayTeam.abbrev}
                        players={players}
                    />
                </>
            )}
        </div>
    );
};

export default NHLGameLogViewer;
